// Tenants › Signups: /api/v1/admin/platform/signups* (API_CONTRACT.md › Signups;
// backend/src/routes/admin/platform-signups.ts). Platform data, not tenant data.
// A signup's id is its own (it outlives its league), not the league's.

import { apiFetch } from './client'
import type { GrowthTemplate } from './growthCampaignTypes'
import type { Account, ISODateTime, Lead, Touch } from './types'
import type { FacetsResponse } from './filters'

const BASE = '/admin/platform'

export const OUTREACH_STATUSES = ['not_contacted', 'emailed', 'replied', 'bounced'] as const
export type OutreachStatus = (typeof OUTREACH_STATUSES)[number]

export interface SignupPerson {
  /** Null once the account is gone: the snapshot stays. */
  admin_user_id: number | null
  name: string | null
  email: string | null
  last_login_at: ISODateTime | null
}

export const SIGNUP_STATES = ['trial', 'locked', 'paid', 'deleted', 'kept', 'other'] as const
export type SignupStateKey = (typeof SIGNUP_STATES)[number]

/** Where a signup stands, and when the cleanup would delete it (backend services/signup-lifecycle.ts). */
export interface SignupLifecycle {
  /** trial / locked (the trial ended) / paid (now a tenant) / deleted / kept / other (suspended or canceled, never paid). */
  state: SignupStateKey
  trial_days_left: number | null
  locked_days: number | null
  /** Money moved through the league: it's never deleted automatically. */
  needs_look: boolean
  /** would: SIGNUP_CLEANUP_ENABLED is off, nothing will happen. scheduled: it will. */
  deletion: { on: ISODateTime; mode: 'would' | 'scheduled'; warned_at: ISODateTime | null } | null
  league_deleted_at: ISODateTime | null
}

export interface OutreachSummary {
  status: OutreachStatus
  /** Real emails. A dry run isn't one. */
  sent: number
  dry_runs: number
  last_email_at: ISODateTime | null
  replied_at: ISODateTime | null
  bounced_at: ISODateTime | null
}

export interface SignupRow {
  /** The signup's id. */
  id: number
  /** id and site_url are null once the league is deleted; the rest is the snapshot. */
  league: { id: number | null; name: string; subdomain: string; sport: string | null; site_url: string | null; created_at: ISODateTime }
  person: SignupPerson
  signed_up_at: ISODateTime
  days_since_signup: number
  /** "google / cpc", a referrer host, "direct", or null when nothing was recorded. */
  source: string | null
  country: string | null
  /** Null once the league is deleted. */
  account: Pick<Account, 'status' | 'is_live' | 'trial_ends_at'> | null
  last_activity_at: ISODateTime | null
  lifecycle: SignupLifecycle
  keep: boolean
  outreach: OutreachSummary
}

export interface SignupListParams {
  q?: string
  outreach?: OutreachStatus
  state?: SignupStateKey
  /** A source label from `sources`, or "unknown". */
  source?: string
  /** `field:op:value` filters (api/filters.ts), ANDed. */
  f?: string[]
  /** Any sortable field from the facets, `-field` for descending. Default newest first. */
  sort?: string
  page?: number
  page_size?: number
}

export interface SignupListResponse {
  signups: SignupRow[]
  total: number
  page: number
  page_size: number
  /** Within the search and the other filters, ignoring the outreach filter. */
  outreach_counts: Record<'all' | OutreachStatus, number>
  /** Every source label, "unknown" for a signup with nothing recorded. */
  sources: string[]
}

export interface SignupAttribution {
  first_touch: Touch | null
  last_touch: Touch | null
  click_ids: { gclid?: string; fbclid?: string; msclkid?: string; ga_client_id?: string }
  country: string | null
}

/** scheduled and removed only on an email to a lead, or the follow-up after signup (the Schedule). */
export type OutreachEmailStatus = 'scheduled' | 'sending' | 'sent' | 'dry_run' | 'failed' | 'removed'

export interface OutreachEmail {
  id: number
  email: string
  signer: string
  signer_name: string
  subject: string
  /** Exactly what was sent (or would have been, on a dry run), with an unsubscribe link's token hidden. */
  body_text: string
  status: OutreachEmailStatus
  gate_reason: 'sending_off' | 'not_on_allowlist' | null
  error: string | null
  template_id: number | null
  sent_by: { id: number; name: string | null; email: string } | null
  sent_at: ISODateTime | null
  /** When a scheduled email goes. */
  scheduled_for: ISODateTime | null
  replied_at: ISODateTime | null
  bounced_at: ISODateTime | null
  bounce_kind: 'hard' | 'soft' | null
  unsubscribed_at: ISODateTime | null
  /** Set on an email to a Growth lead. */
  lead_id: number | null
  /** 'signup_follow_up': the follow-up after signup, which Ops scheduled itself (Templates › Follow-up after signup). */
  automatic: 'signup_follow_up' | null
  created_at: ISODateTime
}

type Actor = { id: number; name: string | null; email: string } | null

export type SignupTimelineItem =
  | { kind: 'signed_up'; at: ISODateTime }
  | { kind: 'league_deleted'; at: ISODateTime; by: 'ops' | 'cleanup' | null }
  | { kind: 'deletion_warned'; at: ISODateTime; delete_on: ISODateTime | null }
  | { kind: 'note'; at: ISODateTime; id: number; body: string; actor: Actor }
  | {
      kind: 'email'; at: ISODateTime; email_id: number; status: OutreachEmailStatus; subject: string; signer_name: string; gate_reason: string | null; error: string | null; actor: Actor
      /** The follow-up after signup, scheduled by Ops itself. */
      automatic: 'signup_follow_up' | null
      /** When a scheduled one goes. */
      scheduled_for: ISODateTime | null
    }
  | { kind: 'reply' | 'stop'; at: ISODateTime; email_id: number | null; snippet: string | null }
  | { kind: 'bounce'; at: ISODateTime; email_id: number | null; bounce_kind: 'hard' | 'soft' | null }

export interface SignupProfile {
  id: number
  league: {
    /** Null once deleted: name, subdomain and sport are then the snapshot. */
    id: number | null
    name: string
    subdomain: string
    custom_domain: string | null
    site_url: string | null
    sport: string | null
    template: { id: string; name: string } | null
    created_at: ISODateTime
    deleted_at: ISODateTime | null
    deleted_by: 'ops' | 'cleanup' | null
  }
  signed_up_at: ISODateTime
  /** Null once the league is deleted. */
  account: Account | null
  last_activity_at: ISODateTime | null
  lifecycle: SignupLifecycle
  /** Ops' Keep switch: the cleanup never deletes this league. */
  keep: boolean
  kept_at: ISODateTime | null
  person: SignupPerson
  /** personal: a free mail provider (gmail.com, outlook.com, ...), so there's no organisation to look up. */
  email_domain: { domain: string; personal: boolean } | null
  source: string | null
  attribution: SignupAttribution | null
  /** The Growth lead on this person's address, if there is one. */
  lead: { id: number; name: string; status: string } | null
  /** On the do-not-contact list: nothing can be sent. */
  suppressed: boolean
  outreach: OutreachSummary & { emails: OutreachEmail[] }
  /** Newest first; signed_up is always last. */
  timeline: SignupTimelineItem[]
}

export interface OrganisationLookup {
  domain: string | null
  status: 'ok' | 'unavailable' | 'personal' | 'none'
  url: string | null
  title: string | null
  description: string | null
  /** The finder's reason when the page couldn't be read: robots, timeout, login, ... */
  outcome: string | null
  checked_at: ISODateTime | null
}

export interface Signer {
  key: string
  name: string
  /** Empty until the owner fills it in. */
  title: string
}

export interface OutreachSending {
  /** dry_run: GROWTH_SENDING_ENABLED is off, so Send records a dry run. */
  mode: 'live' | 'test_list' | 'dry_run'
  smtp_ready: boolean
  message: string
}

export interface OutreachOptions {
  signers: Signer[]
  templates: GrowthTemplate[]
  sending: OutreachSending
  per_minute: number
}

export interface OutreachDraft {
  template_id?: number | null
  signer: string
  subject: string
  body: string
  /** Schedule instead of Send now: an instant a minute to 90 days ahead. Only an email to a lead. */
  send_at?: ISODateTime | null
  /** A reply in a lead's conversation: the Message-ID it answers, so Gmail threads it. */
  reply_to?: string | null
}

export interface OutreachPreview {
  to: string
  from: string
  subject: string
  text: string
  html: string
  signature: string
  missing: string[]
  unknown_fields: string[]
  subject_problem: string | null
  suppressed: boolean
  sending: OutreachSending
  /** An email to a lead: the row is marketing (the lead's checks), but the email ends at the signature like any one-off. */
  marketing?: boolean
  /** Why it can't go to this lead (a lead's preview only). */
  problem?: LeadEmailProblem | null
}

export interface OutreachResult {
  email: OutreachEmail
  /** scheduled: it goes at `email.scheduled_for`, unless it's removed on the Schedule first. */
  outcome: 'sent' | 'dry_run' | 'failed' | 'scheduled'
  repeated: boolean
  gate_reason: 'sending_off' | 'not_on_allowlist' | null
  message: string
  /** A reply to an inquiry only: it moved the inquiry from new to answered. */
  inquiry_answered?: boolean
}

export function listSignups(params: SignupListParams = {}, signal?: AbortSignal): Promise<SignupListResponse> {
  return apiFetch(`${BASE}/signups`, { query: { ...params, q: params.q?.trim() || undefined }, signal })
}

/** The Signups tab's filter picker. */
/** An email to someone who signed up, with the signup it went to (Growth › Self-signups › Emails). */
export interface SignupEmail extends OutreachEmail {
  signup: { id: number; person_name: string | null; league_name: string; league_id: number | null; subdomain: string }
}

export interface SignupEmailsResponse {
  state: 'scheduled' | 'sent'
  page: number
  page_size: number
  total: number
  counts: { scheduled: number; sent: number }
  emails: SignupEmail[]
}

/** Every email to a signup: `scheduled` (not gone yet, soonest first) or `sent` (the rest, newest first). */
export function listSignupEmails(params: { state?: 'scheduled' | 'sent'; page?: number; page_size?: number } = {}, signal?: AbortSignal): Promise<SignupEmailsResponse> {
  return apiFetch(`${BASE}/signups/emails`, { query: params, signal })
}

export function signupFacets(signal?: AbortSignal): Promise<FacetsResponse> {
  return apiFetch(`${BASE}/signups/facets`, { signal })
}

export async function getSignup(id: number, signal?: AbortSignal): Promise<SignupProfile> {
  const r = await apiFetch<{ signup: SignupProfile }>(`${BASE}/signups/${id}`, { signal })
  return r.signup
}

/** Reads the work domain's home page once, through the Lead finder's fetcher, and keeps it. Can take a few seconds. */
export async function signupOrganisation(id: number, signal?: AbortSignal): Promise<OrganisationLookup> {
  const r = await apiFetch<{ organisation: OrganisationLookup }>(`${BASE}/signups/${id}/organisation`, { signal })
  return r.organisation
}

export async function addSignupNote(id: number, body: string) {
  const r = await apiFetch<{ note: { id: number; body: string; created_at: ISODateTime } }>(`${BASE}/signups/${id}/notes`, { method: 'POST', body: { body } })
  return r.note
}

/** Keep on: the cleanup never deletes this league. Audited, and can be undone. */
export async function setSignupKeep(id: number, keep: boolean): Promise<SignupProfile> {
  const r = await apiFetch<{ signup: SignupProfile }>(`${BASE}/signups/${id}/keep`, { method: 'PATCH', body: { keep } })
  return r.signup
}

/** The lead on this address if there is one (`created: false`), else a new one. */
export function addSignupLead(id: number): Promise<{ lead: Lead; created: boolean }> {
  return apiFetch(`${BASE}/signups/${id}/lead`, { method: 'POST' })
}

export function outreachOptions(signal?: AbortSignal): Promise<OutreachOptions> {
  return apiFetch(`${BASE}/signups/outreach-options`, { signal })
}

/** Sends nothing, stores nothing. */
export async function previewOutreach(id: number, draft: OutreachDraft, signal?: AbortSignal): Promise<OutreachPreview> {
  const r = await apiFetch<{ preview: OutreachPreview }>(`${BASE}/signups/${id}/outreach/preview`, { method: 'POST', body: draft, signal })
  return r.preview
}

/**
 * The owner's Send. `confirm: true` is the approval, and only the compose panel's
 * Send button sends it. `request_id` makes a double click one email. A failed send
 * is a 502 `send_failed` whose details hold the email row.
 */
export function sendOutreach(id: number, draft: OutreachDraft & { request_id: string }): Promise<OutreachResult> {
  return apiFetch(`${BASE}/signups/${id}/outreach/send`, { method: 'POST', body: { ...draft, confirm: true } })
}

// ─── A tenant page's Email (the same compose, to one of the league's admins) ─

export interface TenantRecipient {
  admin_user_id: number
  name: string | null
  email: string
  role: string
  /** The one the compose picks first: the signup's owner, else the first admin. */
  default: boolean
}

export interface TenantOutreach {
  /** Empty: the league has no admin with an address (Send is refused, 409 `no_recipient`). */
  recipients: TenantRecipient[]
  /** The league's Signups row, if it has one: its emails show on that profile too. */
  signup_id: number | null
  /** Every one-off email about this league, newest first (its signup's too). */
  emails: OutreachEmail[]
}

export type TenantOutreachDraft = OutreachDraft & { admin_user_id?: number | null }

export function tenantOutreach(leagueId: number, signal?: AbortSignal): Promise<TenantOutreach> {
  return apiFetch(`${BASE}/leagues/${leagueId}/outreach`, { signal })
}

/** Sends nothing, stores nothing. */
export async function previewTenantOutreach(leagueId: number, draft: TenantOutreachDraft, signal?: AbortSignal): Promise<OutreachPreview> {
  const r = await apiFetch<{ preview: OutreachPreview }>(`${BASE}/leagues/${leagueId}/outreach/preview`, { method: 'POST', body: draft, signal })
  return r.preview
}

/** The owner's Send, as for a signup: `confirm: true` only from the Send button, a fresh `request_id` each press. */
export function sendTenantOutreach(leagueId: number, draft: TenantOutreachDraft & { request_id: string }): Promise<OutreachResult> {
  return apiFetch(`${BASE}/leagues/${leagueId}/outreach/send`, { method: 'POST', body: { ...draft, confirm: true } })
}

// ─── A lead's Email (the same compose, to a Growth lead, as marketing) ──────

export interface LeadEmailProblem {
  code: 'no_address' | 'suppressed' | 'not_us'
  message: string
}

export interface LeadOutreach {
  /** The lead's address, or null when it has none we can email. */
  to: string | null
  /** Why nothing can go to this lead; Send is refused with the same code (409). */
  problem: LeadEmailProblem | null
  /** The lead's serving preview, which {{preview_url}} fills. */
  preview_url: string | null
  /** Every one-off email to this lead, newest first (scheduled ones too). */
  emails: OutreachEmail[]
}

export function leadOutreach(leadId: number, signal?: AbortSignal): Promise<LeadOutreach> {
  return apiFetch(`${BASE}/growth/leads/${leadId}/outreach`, { signal })
}

/** Sends nothing, stores nothing. The text ends at the signature: an email to a lead has no footer. */
export async function previewLeadOutreach(leadId: number, draft: OutreachDraft, signal?: AbortSignal): Promise<OutreachPreview> {
  const r = await apiFetch<{ preview: OutreachPreview }>(`${BASE}/growth/leads/${leadId}/outreach/preview`, { method: 'POST', body: draft, signal })
  return r.preview
}

/** The owner's Send now, or Schedule with `send_at`: `confirm: true` only from the compose's dialog, a fresh `request_id` each press. */
export function sendLeadOutreach(leadId: number, draft: OutreachDraft & { request_id: string }): Promise<OutreachResult> {
  return apiFetch(`${BASE}/growth/leads/${leadId}/outreach/send`, { method: 'POST', body: { ...draft, confirm: true } })
}

// ─── An inquiry's Reply (the same compose, to the address that wrote to us) ─

export interface InquiryOutreach {
  /** The address that wrote to us, or null when it isn't one we can email. */
  to: string | null
  /** On the do-not-contact list: nothing goes to them (Send is refused, 409 `suppressed`). */
  suppressed: boolean
  /** The subject a Reply starts with: "Re: " and their topic, or "Re: your message to ScoreLM". */
  subject: string
  /** Every reply to this inquiry, newest first. */
  emails: OutreachEmail[]
}

export function inquiryOutreach(inquiryId: number, signal?: AbortSignal): Promise<InquiryOutreach> {
  return apiFetch(`${BASE}/growth/inquiries/${inquiryId}/outreach`, { signal })
}

/** Sends nothing, stores nothing. 1:1, not marketing: no footer, no unsubscribe link. */
export async function previewInquiryOutreach(inquiryId: number, draft: OutreachDraft, signal?: AbortSignal): Promise<OutreachPreview> {
  const r = await apiFetch<{ preview: OutreachPreview }>(`${BASE}/growth/inquiries/${inquiryId}/outreach/preview`, { method: 'POST', body: draft, signal })
  return r.preview
}

/** The owner's Send, now (a reply can't be scheduled): `confirm: true` only from the compose's dialog, a fresh `request_id` each press. */
export function sendInquiryOutreach(inquiryId: number, draft: OutreachDraft & { request_id: string }): Promise<OutreachResult> {
  return apiFetch(`${BASE}/growth/inquiries/${inquiryId}/outreach/send`, { method: 'POST', body: { ...draft, confirm: true } })
}
